// src/components/lesson/ScenarioSelectTemplate.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import LessonShell from "./LessonShell";
import { secureKnowledgeChecksEnabled, verifyKnowledgeCheck } from "../../lib/learning/secureKnowledgeChecks";
import KnowledgeCheckFeedback from "./KnowledgeCheckFeedback";

import {
  getKnowledgeCheckProgress,
  incrementKnowledgeCheckWrongAttempt,
  saveCorrectSelectAnswer,
} from "../../lib/learning/knowledgeCheckProgress";

const TEMP_CHARACTER_IMAGE =
  "https://placehold.co/500x650/F3F3F3/999999?text=Replace+Character";

function idsEqual(a, b) {
  return String(a ?? "") === String(b ?? "");
}

function makeSafeId(value, fallback) {
  const text = String(value || "").trim();

  if (!text) {
    return fallback;
  }

  return text
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function getOptionId(option, index) {
  return (
    option?.id ??
    option?._key ??
    option?.value ??
    makeSafeId(
      option?.answerText ||
        option?.optionText ||
        option?.title ||
        option?.label ||
        option?.text,
      `option-${index + 1}`
    )
  );
}

function getOptionLabel(option) {
  return (
    option?.title ??
    option?.label ??
    option?.text ??
    option?.answerText ??
    option?.optionText ??
    ""
  );
}

function isOptionCorrect(option) {
  if (option?.isCorrect === true || option?.correct === true) {
    return true;
  }

  const status = String(
    option?.answerStatus ||
      option?.status ||
      option?.correctAnswer ||
      ""
  )
    .trim()
    .toLowerCase();

  return status === "correct" || status === "true" || status === "yes";
}

function normalizeCopy(value) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, " ");
}

function removeRepeatedScenarioFromPrompt({ scenarioText, prompt }) {
  const scenario = normalizeCopy(scenarioText);
  const rawPrompt = normalizeCopy(prompt);

  if (!scenario || !rawPrompt) {
    return rawPrompt;
  }

  const scenarioLower = scenario.toLowerCase();
  const promptLower = rawPrompt.toLowerCase();

  if (!promptLower.startsWith(scenarioLower)) {
    return rawPrompt;
  }

  return rawPrompt
    .slice(scenario.length)
    .trim()
    .replace(/^[.:\-\s]+/, "")
    .trim();
}

export default function ScenarioSelectTemplate({
  questionKey,
  secureAssessment,

  title = "",
  prompt = "",

  options = [],

  correctFeedbackText = "",
  incorrectFeedbackText = "",
  incorrectSupportText = "",

  characterImageSrc = TEMP_CHARACTER_IMAGE,
  characterImageAlt = "Scenario character",
  scenarioText = "",

  onNext,
  onIncorrectRetry,

  ctaNextLabel = "Next",

  backTo,
  backLabel = "Back",
  onBack,

  conceptNumber = 1,
  conceptTotal = 1,

  progressStep = 1,
  progressTotal = 11,

  showTitle = true,

  resetOnMount = false,
  storageKey,
}) {
  const navigate = useNavigate();

  const { questId, subquestId, levelKey } = useParams();

  const normalizedOptions = useMemo(() => {
    return (Array.isArray(options) ? options : []).map((option, index) => {
      const uid = getOptionId(option, index);
      const label = getOptionLabel(option);

      return {
        ...option,
        __uid: uid,
        __label: label,
        __correct: isOptionCorrect(option),
      };
    });
  }, [options]);

  const resolvedQuestionKey =
    questionKey || storageKey || title || prompt || "knowledge-check";

  const storageContext = useMemo(
    () => ({
      questId,
      subquestId,
      levelKey,
      questionKey: resolvedQuestionKey,
    }),
    [questId, subquestId, levelKey, resolvedQuestionKey]
  );

  const [selectedUid, setSelectedUid] = useState(null);
  const [lockedCorrectAnswerId, setLockedCorrectAnswerId] = useState(null);
  const [answerLocked, setAnswerLocked] = useState(false);
  const [feedbackState, setFeedbackState] = useState("answering");
  const [verifying, setVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState("");
  const secureMode = secureKnowledgeChecksEnabled && !!secureAssessment;

  useEffect(() => {
    setFeedbackState("answering");

    if (secureMode) {
      setSelectedUid(null);
      setLockedCorrectAnswerId(null);
      setAnswerLocked(false);
      return;
    }

    if (resetOnMount) {
      setSelectedUid(null);
      setLockedCorrectAnswerId(null);
      setAnswerLocked(false);
      return;
    }

    const saved = getKnowledgeCheckProgress(storageContext);

    if (saved?.completedCorrectly && saved?.correctAnswerId != null) {
      const savedOption = normalizedOptions.find((option) =>
        idsEqual(option.__uid, saved.correctAnswerId)
      );

      if (savedOption && savedOption.__correct) {
        setSelectedUid(savedOption.__uid);
        setLockedCorrectAnswerId(savedOption.__uid);
        setAnswerLocked(true);
        return;
      }
    }

    setSelectedUid(null);
    setLockedCorrectAnswerId(null);
    setAnswerLocked(false);
  }, [storageContext, normalizedOptions, resetOnMount, secureMode]);

  const selectedOption =
    normalizedOptions.find((option) => idsEqual(option.__uid, selectedUid)) ||
    null;

  const handleSelect = (event, option) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    if (answerLocked) {
      return;
    }

    setSelectedUid(option.__uid);
  };

  const handleSubmit = async () => {
    if (verifying || !selectedOption) {
      return;
    }

    if (answerLocked) {
      setFeedbackState("correct");
      return;
    }

    if (secureMode) {
      setVerifying(true);
      setVerifyError("");
      try {
        const result = await verifyKnowledgeCheck({
          ...secureAssessment,
          answer: { optionKey: String(selectedOption.__uid) },
        });
        if (result.correct) {
          setLockedCorrectAnswerId(selectedOption.__uid);
          setAnswerLocked(true);
          setFeedbackState("correct");
        } else {
          incrementKnowledgeCheckWrongAttempt(storageContext);
          setFeedbackState("incorrect");
        }
      } catch (error) {
        setVerifyError(error?.message || "Could not verify the answer. Please try again.");
      } finally {
        setVerifying(false);
      }
      return;
    }

    if (selectedOption.__correct) {
      saveCorrectSelectAnswer(storageContext, selectedOption.__uid);

      setLockedCorrectAnswerId(selectedOption.__uid);
      setAnswerLocked(true);
      setFeedbackState("correct");
      return;
    }

    incrementKnowledgeCheckWrongAttempt(storageContext);
    setFeedbackState("incorrect");
  };

  const handleRetry = () => {
    setFeedbackState("answering");

    if (onIncorrectRetry) {
      onIncorrectRetry();
      return;
    }

    setSelectedUid(null);
  };

  const handleContinue = () => {
    setFeedbackState("answering");
    onNext?.();
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }

    if (backTo) {
      navigate(backTo);
      return;
    }

    navigate(-1);
  };

  if (feedbackState === "correct" || feedbackState === "incorrect") {
    return (
      <KnowledgeCheckFeedback
        status={feedbackState}
        correctFeedbackText={correctFeedbackText}
        incorrectFeedbackText={incorrectFeedbackText}
        incorrectSupportText={incorrectSupportText}
        onContinue={handleContinue}
        onRetry={handleRetry}
        progressStep={progressStep}
        progressTotal={progressTotal}
      />
    );
  }

  const canSubmit = selectedUid != null && !verifying;

  const displayScenario = scenarioText || title || "";

  const displayPrompt = removeRepeatedScenarioFromPrompt({
    scenarioText: displayScenario,
    prompt,
  });

  return (
    <LessonShell
      progressStep={progressStep}
      progressTotal={progressTotal}
      showBack
      backLabel={backLabel}
      onBack={handleBack}
      showNext
      nextLabel={ctaNextLabel}
      onNext={handleSubmit}
      nextDisabled={!canSubmit}
      contentClassName="pt-8"
    >
      <p className="text-center text-[20px] font-extrabold uppercase tracking-[-0.01em] text-[#38B6E8]">
        Knowledge Check
      </p>

      {verifyError && (
        <p role="alert" className="mx-auto mt-4 max-w-xl text-center text-sm text-red-700">
          {verifyError}
        </p>
      )}
      {verifying && (
        <p role="status" className="mt-3 text-center text-sm">Checking your answer...</p>
      )}

      <section
        className={[
          "relative",
          "z-[2]",
          "mx-auto",
          "mt-10",
          "grid",
          "w-full",
          "max-w-[580px]",
          "grid-cols-[34%_1fr]",
          "items-center",
          "gap-4",
          "sm:mt-12",
          "sm:grid-cols-[190px_1fr]",
          "sm:gap-7",
        ].join(" ")}
      >
        {!!characterImageSrc && (
          <div className="flex items-end justify-center">
            <img
              src={characterImageSrc}
              alt={characterImageAlt}
              className="mx-auto h-auto max-h-[220px] w-full max-w-[150px] object-contain sm:max-h-[260px] sm:max-w-[190px]"
              loading="eager"
              decoding="async"
              draggable="false"
            />
          </div>
        )}

        <div className="pb-3">
          {!!displayScenario && (
            <p className="text-[17px] font-medium leading-[1.45] text-[#171717] sm:text-[19px]">
              {displayScenario}
            </p>
          )}

          {!!displayPrompt && (
            <p
              className={[
                "text-[18px]",
                "font-extrabold",
                "leading-[1.35]",
                "text-[#111111]",
                "sm:text-[20px]",
                displayScenario ? "mt-6" : "",
              ].join(" ")}
            >
              {displayPrompt}
            </p>
          )}
        </div>
      </section>

      <section className="relative z-[5] mx-auto mt-12 w-full max-w-[590px] space-y-5">
        {normalizedOptions.map((option, index) => {
          const selected = idsEqual(option.__uid, selectedUid);

          return (
            <button
              key={`${option.__uid}-${index}`}
              type="button"
              onClick={(event) => handleSelect(event, option)}
              disabled={answerLocked}
              aria-pressed={selected}
              className={[
                "relative",
                "z-[5]",
                "flex",
                "min-h-[72px]",
                "w-full",
                "items-center",
                "rounded-[17px]",
                "border-[3px]",
                "bg-white",
                "px-5",
                "py-4",
                "text-left",
                "transition-all",
                "pointer-events-auto",

                selected
                  ? "border-[#FF914D] text-[#FF914D]"
                  : "border-[#D7D7D7] text-[#151515]",

                answerLocked
                  ? "cursor-default"
                  : "cursor-pointer hover:border-[#FFB07A] active:scale-[0.99]",
              ].join(" ")}
            >
              <span
                className={[
                  "mr-6",
                  "flex",
                  "h-[34px]",
                  "w-[34px]",
                  "shrink-0",
                  "items-center",
                  "justify-center",
                  "rounded-full",
                  "border-[3px]",
                  "transition-all",
                  selected ? "border-[#D6D6D6]" : "border-[#D8D8D8]",
                ].join(" ")}
                aria-hidden="true"
              >
                {selected && (
                  <span className="h-[24px] w-[24px] rounded-full bg-[#FF914D]" />
                )}
              </span>

              <span className="text-[15px] font-medium leading-[1.45] sm:text-[16px]">
                {option.__label}
              </span>
            </button>
          );
        })}
      </section>

      {answerLocked && lockedCorrectAnswerId != null && (
        <p className="mx-auto mt-5 max-w-[560px] text-center text-xs font-medium text-[#8A8A8A]">
          You already completed this Knowledge Check.
        </p>
      )}
    </LessonShell>
  );
}